import React from 'react';
import { visuallyHidden } from '@mui/utils';

/**
 * Style object that hides content visually while keeping it available to
 * screen readers. Re-exported from MUI so there is a single canonical import.
 */
export const srOnly = visuallyHidden;

/**
 * Makes a non-interactive element (div/span) behave like a button for keyboard
 * and screen-reader users: focusable, exposed as a button, and activated by
 * Enter/Space in addition to click. Spread onto the element and pass an
 * accessible label. Purely additive — the existing onClick keeps working.
 */
export const activatable = (
  onActivate: (e: React.SyntheticEvent) => void,
  label: string,
) => ({
  role: 'button' as const,
  tabIndex: 0,
  'aria-label': label,
  onClick: onActivate,
  onKeyDown: (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onActivate(e);
    }
  },
});

/**
 * True when the user has asked the OS to minimize non-essential motion.
 * CSS animations are neutralized globally in styles/index.css; use this for
 * JS-driven motion such as scrollIntoView({ behavior }).
 */
export const prefersReducedMotion = (): boolean => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/** Scroll behavior honoring the user's reduced-motion preference. */
export const scrollBehavior = (): ScrollBehavior => (prefersReducedMotion() ? 'auto' : 'smooth');
